import { AxiosResponse } from 'axios';
import {
  SET_ORGANIZATIONS,
  SET_ORGANIZATION,
  SET_ORGANIZATION_STATS,
  SET_ERRORMSG,
  SET_ACCOUNT,
  MERGE_ORGANIZATION,
} from '@/store/mutation_types';
import {
  CLEAR_ORGANIZATION_ISLOADING,
  DELETE_ORGANIZATION,
  EDIT_ORGANIZATION,
  GET_ORGANIZATION,
  GET_ORGANIZATION_STATS,
  GET_ORGANIZATIONS,
  REGISTER_ORGANIZATION,
  REQUEST_ORGANIZATION_DELETION_CODE,
  SET_ORGANIZATION_ARCHIVED,
  SIGNUP_ORGANIZATION,
  TOGGLE_ORGANIZATION_IS_ACTIVE,
  SET_ORGANIZATION_APPS,
  SET_ORGANIZATION_STATUS,
  ARCHIVE_ORGANIZATION,
} from '@/store/action_types';
import { StateObject } from '@/dtos/state.dto';
import Organization from '@/services/organization';
import { Module } from 'vuex';
import {
  EditOrganization,
  RegisterOrganization,
  GetOrganization,
  SetOrganizationArchived,
} from '@/dtos/organization.dto';

// What the deletion steps resolve to: whether it worked, the server's words
// when it didn't (shown in the dialog), and its error code.
const deletionAnswer = (result: AxiosResponse | undefined) => ({
  ok: Boolean(result && result.data && result.data.status),
  message: (result && result.data && result.data.message) || 'Something went wrong. Try again.',
  errorCode: (result && result.data && result.data.error && result.data.error.errorCode) || null,
  data: (result && result.data && result.data.data) || null,
});

// A Settings tab change: the server answers with the whole organization, laid
// over what's shown. Resolves to whether it worked. The API's own errors are
// already toasted by its interceptor; one that never got an answer is not.
const saveOrganizationChange = async (commit: any, request: () => Promise<AxiosResponse>) => {
  try {
    const result: AxiosResponse = await request();
    if (result && result.data.status) {
      commit(MERGE_ORGANIZATION, result.data.data);
      return true;
    }
  } catch (e) {
    commit(SET_ERRORMSG, "Couldn't reach the server. Check your connection and try again.");
  }
  return false;
};

// Bumped for every list request, so a slow answer to an older search or page
// can't overwrite the list a newer one asked for.
let organizationsRequestId = 0;

const organization: Module<any, {}> = {
  state: {
    organization: {
      data: null,
      isLoading: false,
    },
    organizations: {
      data: null,
      metaData: null,
      isLoading: false,
    },
    // Platform-wide counts from `GET /organization/stats`, counted by the
    // server across every organization (not just the page on show).
    organizationStats: {
      data: null,
      isLoading: false,
    },
  },
  getters: {
    organization: (state: { organization: any }) => state.organization,
    organizations: (state: { organizations: any }) => state.organizations,
    organizationStats: (state: { organizationStats: any }) => state.organizationStats,
  },
  mutations: {
    [SET_ORGANIZATION]: (
      state: { organization: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      payload.data != null ? (state.organization.data = payload.data) : null,
        (state.organization.isLoading = payload.isLoading);
    },
    // What the server answered after a change, laid over the organization on
    // the page and its row in the list — never a stand-in with only an id, so
    // the header keeps its name, logo and status while a change is saving.
    [MERGE_ORGANIZATION]: (state: any, changes: any) => {
      if (!changes || !changes.id) return;
      const current = state.organization.data;
      if (current && current.id === changes.id) {
        state.organization.data = { ...current, ...changes };
      }
      const rows = state.organizations.data;
      if (Array.isArray(rows)) {
        const index = rows.findIndex((row: any) => row.id === changes.id);
        if (index >= 0) rows.splice(index, 1, { ...rows[index], ...changes });
      }
    },
    [SET_ORGANIZATIONS]: (
      state: { organizations: StateObject },
      payload: { data: null; metaData: any; isLoading: any }
    ) => {
      payload.data != null ? (state.organizations.data = payload.data) : null,
        payload.metaData != null
          ? (state.organizations.metaData = payload.metaData)
          : null,
        (state.organizations.isLoading = payload.isLoading);
    },
    [SET_ORGANIZATION_STATS]: (
      state: { organizationStats: StateObject },
      payload: { data?: any; isLoading: any }
    ) => {
      payload.data != null ? (state.organizationStats.data = payload.data) : null,
        (state.organizationStats.isLoading = payload.isLoading);
    },
  },
  actions: {
    [REGISTER_ORGANIZATION]: async ({ commit }: any, payload: FormData) => {
      commit(SET_ORGANIZATION, { data: null, isLoading: true });

      const result: AxiosResponse =
        await Organization.registerOrganization(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_ORGANIZATION, {
            data: result.data.data,
            isLoading: false,
          });
          return true;
        }
        commit(SET_ORGANIZATION, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ORGANIZATION, { isLoading: false });
        return false;
      }
    },
    [SIGNUP_ORGANIZATION]: async ({ commit }: any, payload: any) => {
      commit(SET_ORGANIZATION, { data: null, isLoading: true });

      const result: AxiosResponse = await Organization.signupOrganization(
        payload as RegisterOrganization
      );

      if (result) {
        if (result.data.status) {
          commit(SET_ACCOUNT, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_ORGANIZATION, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ORGANIZATION, { isLoading: false });
        return false;
      }
    },
    [GET_ORGANIZATION]: async ({ commit }: any, payload: any) => {
      commit(SET_ORGANIZATION, { data: null, isLoading: true });

      const result: AxiosResponse = await Organization.getOrganizations(
        payload as GetOrganization
      );

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_ORGANIZATION, {
            data: result.data.data[0],
            isLoading: false,
          });
          return true;
        }
        commit(SET_ORGANIZATION, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ORGANIZATION, { isLoading: false });
        return false;
      }
    },
    [GET_ORGANIZATIONS]: async ({ commit }: any, payload: any) => {
      const requestId = ++organizationsRequestId;
      commit(SET_ORGANIZATIONS, { data: null, isLoading: true });

      const result: AxiosResponse = await Organization.getOrganizations(
        payload as GetOrganization
      );

      // A newer request has gone out since; its answer is the one to show.
      if (requestId !== organizationsRequestId) return false;

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_ORGANIZATIONS, {
            data: result.data.data,
            metaData: result.data.metaData,
            isLoading: false,
          });
          return true;
        }
        commit(SET_ORGANIZATIONS, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ORGANIZATIONS, { isLoading: false });
        return false;
      }
    },
    [GET_ORGANIZATION_STATS]: async ({ commit }: any) => {
      commit(SET_ORGANIZATION_STATS, { isLoading: true });

      try {
        const result: AxiosResponse = await Organization.getOrganizationStats();

        if (result && result.data.status) {
          commit(SET_ORGANIZATION_STATS, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_ORGANIZATION_STATS, { isLoading: false });
        return false;
      } catch (e) {
        // Network failures reject instead of resolving empty.
        commit(SET_ORGANIZATION_STATS, { isLoading: false });
        return false;
      }
    },
    [EDIT_ORGANIZATION]: async (
      { commit, rootState }: any,
      payload: FormData
    ) => {
      commit(SET_ORGANIZATION, { data: null, isLoading: true });

      const result: AxiosResponse =
        await Organization.editOrganization(payload);

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_ORGANIZATION, {
            data: result.data.data,
            isLoading: false,
          });
          rootState.account.account.data.organization = result.data.data;

          return true;
        }
        commit(SET_ORGANIZATION, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ORGANIZATION, { isLoading: false });
        return false;
      }
    },
    [TOGGLE_ORGANIZATION_IS_ACTIVE]: async (
      { commit, state }: any,
      payload: EditOrganization
    ) => {
      commit(SET_ORGANIZATION, { data: { id: payload.id }, isLoading: true });

      let result: AxiosResponse = await Organization.toggleOrganizationIsActive(
        payload as EditOrganization
      );

      if (result) {
        if (result.data.status) {
          let index;
          if (state.organizations.data) {
            state.organizations.data.forEach((e: any, i: number) => {
              if (e.id == result.data.data.id) {
                index = i;
              }
            });
            if (index != null)
              state.organizations.data[index].isActive = result.data.data.isActive;
          }

          commit(SET_ORGANIZATION, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_ORGANIZATION, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ORGANIZATION, { isLoading: false });
        return false;
      }
    },
    // Archives (`isArchived: true`) or unarchives an organization.
    [SET_ORGANIZATION_ARCHIVED]: async ({ commit }: any, payload: SetOrganizationArchived) => {
      // The row being worked on, so the list can show it busy.
      commit(SET_ORGANIZATION, { data: { id: payload.id }, isLoading: true });

      const result: AxiosResponse = await Organization.setOrganizationArchived(payload);

      if (result && result.data.status) {
        commit(SET_ORGANIZATION, { data: result.data.data, isLoading: false });
        return true;
      }
      commit(SET_ORGANIZATION, { isLoading: false });
      return false;
    },
    // Step one of deleting: email the signed-in admin a 6-digit code.
    [REQUEST_ORGANIZATION_DELETION_CODE]: async (_context: any, id: string) => {
      try {
        return deletionAnswer(await Organization.requestOrganizationDeletionCode(id));
      } catch (e) {
        return deletionAnswer(undefined);
      }
    },
    // Step two: delete, with the code from the email.
    [DELETE_ORGANIZATION]: async ({ commit, state }: any, payload: { id: string; code: string }) => {
      // The row being worked on, for the list. When it's the organization open
      // on its own page (the Settings tab), it stays whole, so that page keeps
      // showing it behind the dialog — through a wrong code, too.
      const open = state.organization.data;
      commit(SET_ORGANIZATION, { data: open && open.id === payload.id ? open : { id: payload.id }, isLoading: true });
      let answer;
      try {
        answer = deletionAnswer(await Organization.deleteOrganization(payload.id, payload.code));
      } catch (e) {
        answer = deletionAnswer(undefined);
      }
      commit(SET_ORGANIZATION, { isLoading: false });
      return answer;
    },
    // The Settings tab's changes. Each resolves to whether it worked; the page
    // shows its own busy state, and the header picks the answer up.
    [SET_ORGANIZATION_APPS]: ({ commit }: any, payload: { id: string; apps: string[] }) =>
      saveOrganizationChange(commit, () => Organization.setOrganizationApps(payload.id, payload.apps)),
    [SET_ORGANIZATION_STATUS]: ({ commit }: any, payload: { id: string; isActive: boolean }) =>
      saveOrganizationChange(commit, () => Organization.setOrganizationStatus(payload.id, payload.isActive)),
    [ARCHIVE_ORGANIZATION]: ({ commit }: any, payload: SetOrganizationArchived) =>
      saveOrganizationChange(commit, () => Organization.setOrganizationArchived(payload)),
    [CLEAR_ORGANIZATION_ISLOADING]: ({ commit }) => {
      commit(SET_ORGANIZATION, { isLoading: false });
      commit(SET_ORGANIZATIONS, { isLoading: false });
      commit(SET_ORGANIZATION_STATS, { isLoading: false });
    },
  },
};

export default organization;
